import { addCache } from 'api/caching'
import { getCerebroHeaders } from 'api/Cerebro/headers'
import { orgScopedApiClient } from 'api/orgScopedClient'
import {
    ChildOrganization,
    Member,
    Organization,
    OrganizationGroup,
    OrganizationGroupRequest,
    OrganizationInvitation,
    UserGroupType,
} from 'types'

import {
    CerebroApiOptionsHeaders,
    CerebroPaginatedResponse,
    CerebroResourceResponse,
} from '../types'

export interface NewOrganization {
    name: string
    salesforce_id?: string
    trial_expiration_date?: string | null
    admin_invite_emails?: string[]
}

export type OrganizationUpdatePayload = Partial<
    Omit<Organization, 'fiscal_year_start_month'> & {
        // PATCH uses null to clear; Organization stores undefined when unset.
        fiscal_year_start_month?: number | null
    }
>

export const createOrganization = (
    data: NewOrganization
): Promise<CerebroResourceResponse<Organization>> =>
    orgScopedApiClient.post('/api/user/organizations/', data)

export const getOrganization = (
    organizationId: string,
    options: { headers: CerebroApiOptionsHeaders }
): Promise<CerebroResourceResponse<Organization>> =>
    orgScopedApiClient.get(`/api/organization/${organizationId}/`, {
        headers: getCerebroHeaders(options.headers),
    })
addCache(getOrganization)

export const updateOrganization = (
    organizationId: string,
    data: OrganizationUpdatePayload
): Promise<CerebroResourceResponse<Organization>> =>
    orgScopedApiClient.patch(`/api/organization/${organizationId}/`, data)

export const getOrganizationGroups = (
    organizationId: string,
    params: Record<string, unknown>
): Promise<CerebroPaginatedResponse<OrganizationGroup>> =>
    orgScopedApiClient.get(`/api/organization/${organizationId}/groups/`, {
        params,
    })
addCache(getOrganizationGroups)

export const getOrganizationMembers = (
    organizationId: string | undefined,
    params: Record<string, unknown>
): Promise<CerebroPaginatedResponse<Member>> | null => {
    return organizationId
        ? orgScopedApiClient.get(
              `/api/organization/${organizationId}/members/`,
              {
                  params,
              }
          )
        : null
}
addCache(getOrganizationMembers)

export const updateOrganizationMember = (
    organizationId: string,
    memberId: string,
    data: Partial<Member>
): Promise<CerebroResourceResponse<Member>> =>
    orgScopedApiClient.patch(
        `/api/organization/${organizationId}/member/${memberId}/`,
        data
    )

export const getOrganizationGroupMembers = (
    organizationGroupId: string
): Promise<CerebroPaginatedResponse<Member>> =>
    orgScopedApiClient.get(
        `/api/organization_group/${organizationGroupId}/members/`
    )
addCache(getOrganizationGroupMembers)

export const getOrganizationBrands = (
    params: Record<string, unknown>,
    options: { headers?: CerebroApiOptionsHeaders } = {}
): Promise<CerebroPaginatedResponse<{ brand: string }>> =>
    orgScopedApiClient.get(`/api/organization_brands/`, {
        params,
        headers: getCerebroHeaders(options.headers),
    })

interface InviteMemberData {
    user_email: string
    organization_id: string
    organization_group_ids: string[]
    member_type: UserGroupType
}

export const inviteMember = (
    data: InviteMemberData
): Promise<CerebroResourceResponse<OrganizationInvitation>> =>
    orgScopedApiClient.post('/api/invitations/', data)

export const resendInvite = (
    inviteId: string
): Promise<CerebroResourceResponse<OrganizationInvitation>> =>
    orgScopedApiClient.post(`/api/invitation/${inviteId}/resend/`)

export const updateInvite = (
    inviteId: string,
    data: Partial<OrganizationInvitation>
): Promise<CerebroResourceResponse<OrganizationInvitation>> =>
    orgScopedApiClient.patch(`/api/invitation/${inviteId}/`, data)

export const getInvitationByToken = (
    token: string
): Promise<CerebroResourceResponse<OrganizationInvitation>> =>
    orgScopedApiClient.get(`/api/invitation/token/${token}/`)

export const getOrganizationInvitations = (
    organizationId: string,
    params: Record<string, unknown>
): Promise<CerebroPaginatedResponse<OrganizationInvitation>> =>
    orgScopedApiClient.get(`/api/organization/${organizationId}/invitations/`, {
        params,
    })
addCache(getOrganizationInvitations)

export const getOrganizationChildren = (
    organizationId: string | number,
    params: Record<string, unknown>
): Promise<CerebroPaginatedResponse<ChildOrganization>> =>
    orgScopedApiClient.get(`/api/organization/${organizationId}/child_orgs/`, {
        params,
    })
addCache(getOrganizationChildren)

export const getOrganizationChildrenSyncExport = (
    organizationId: string,
    params: Record<string, unknown>
): Promise<CerebroResourceResponse<any>> =>
    orgScopedApiClient.get(
        `/api/organization/${organizationId}/child_orgs/export/csv/`,
        {
            headers: {
                Accept: 'text/csv',
            },
            params,
        }
    )

export const getOrganizationGroup = (
    organizationGroupId: string
): Promise<CerebroResourceResponse<OrganizationGroup>> =>
    orgScopedApiClient.get(`/api/organization_group/${organizationGroupId}/`)
addCache(getOrganizationGroup)

export const deleteOrganizationGroup = (
    organizationGroupId: string
): Promise<CerebroResourceResponse<void>> =>
    orgScopedApiClient.delete(`/api/organization_group/${organizationGroupId}/`)

export const patchOrganizationGroup = (
    organizationGroupId: string,
    params: Partial<OrganizationGroupRequest>
): Promise<CerebroResourceResponse<OrganizationGroup>> =>
    orgScopedApiClient.patch(
        `/api/organization_group/${organizationGroupId}/`,
        params
    )

export const removeMemberFromOrganization = (
    organizationId: string,
    memberId: string
): Promise<CerebroResourceResponse<void>> =>
    orgScopedApiClient.delete(
        `/api/organization/${organizationId}/member/${memberId}/`
    )

export const createOrganizationGroup = (
    data: Partial<OrganizationGroupRequest>
): Promise<CerebroResourceResponse<OrganizationGroup>> =>
    orgScopedApiClient.post('/api/organization_groups/', data)
